import { motion } from "framer-motion";
import {
  Zap,
  RotateCw,
  Trophy,
  Stethoscope,
  LineChart,
  Pill,
  ArrowUpRight,
} from "lucide-react";
import { TEST_IDS } from "@/lib/testIds";
import { waLink } from "@/lib/contact";

const services = [
  {
    icon: Zap,
    title: "Metabolik Beslenme",
    desc:
      "Antrenman yükü ve yoğunluğuna (Training Load) göre karbonhidrat ve enerji alımı planlaması. Aerobik ve anaerobik sistemlere özel makro dağılımı.",
  },
  {
    icon: RotateCw,
    title: "İnsülin Direnci",
    desc:
      "Kan şekeri regülasyonu ve insülin hassasiyetini iyileştirmek için kanıta dayalı klinik beslenme protokolleri.",
  },
  {
    icon: Trophy,
    title: "Futbolcu Beslenmesi",
    desc:
      "FC Barcelona metodolojisiyle; maç öncesi hazırlık, maç sırası enerji devamlılığı ve devre arası beslenme protokolleri.",
  },
  {
    icon: Stethoscope,
    title: "Klinik Diyetetik",
    desc:
      "Biyokimyasal bulgular ve laboratuvar verileri ışığında kişiye özel, medikal destekli diyet programları.",
  },
  {
    icon: LineChart,
    title: "Multidisipliner Yaklaşım",
    desc:
      "Klinik, sporcu ve metabolik verileri tek bir bütünsel beslenme planına dönüştüren çok boyutlu uzmanlık yaklaşımı.",
  },
  {
    icon: Pill,
    title: "Supplement Danışmanlığı",
    desc:
      "Kanıta dayalı supplement protokolü. Hangi ürünlerin işe yaradığını, hangilerinin boşa para olduğunu biliyor, kişiye özel öneri sunuyorum.",
  },
];

export const Services = () => {
  return (
    <section
      id="hizmetler"
      className="relative py-24 md:py-32 bg-mesh-soft border-t border-[#0F2D2A]/8 overflow-hidden"
    >
      <div className="blob delay-1" style={{ width: 380, height: 380, top: 80, right: -120, background: "radial-gradient(circle, rgba(14,165,233,0.25), transparent 70%)" }} />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
          >
            <p className="label-eyebrow mb-4">03 — Hizmetler</p>
            <h2 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#0F2D2A] leading-[1.02]">
              Sporcudan klinik <br />
              danışana,{" "}
              <span className="bg-gradient-to-r from-[#10B981] to-[#0EA5E9] bg-clip-text text-transparent">
                altı protokol.
              </span>
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-base md:text-lg text-[#1E4842] max-w-md"
          >
            Her hizmet kanıta dayalı bir protokol içerir. Detay için WhatsApp
            üzerinden ulaşın — size özel plan hazırlayalım.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {services.map((s, i) => {
            const Icon = s.icon;
            const msg = `Merhaba Berke, ${s.title} hizmeti hakkında bilgi almak istiyorum.`;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: (i % 3) * 0.08 }}
                whileHover={{ y: -8 }}
                data-testid={TEST_IDS.services.card(i)}
                className="group relative rounded-3xl bg-white border border-[#0F2D2A]/8 p-7 md:p-8 flex flex-col overflow-hidden shadow-[0_18px_50px_-30px_rgba(15,45,42,0.2)] transition-shadow duration-500 hover:shadow-[0_28px_70px_-30px_rgba(14,165,233,0.35)]"
              >
                {/* Gradient corner */}
                <div className="absolute -top-20 -right-20 w-48 h-48 rounded-full bg-gradient-to-br from-[#10B981]/30 to-[#0EA5E9]/20 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                <div className="relative flex items-start justify-between mb-7">
                  <div className="w-14 h-14 inline-flex items-center justify-center rounded-2xl bg-gradient-to-br from-[#10B981] to-[#0EA5E9] text-white shadow-[0_10px_30px_-12px_rgba(14,165,233,0.55)] group-hover:rotate-6 transition-transform duration-500">
                    <Icon className="w-6 h-6" strokeWidth={2} />
                  </div>
                  <span className="font-heading font-black text-xs text-[#0F2D2A]/30 tracking-widest">
                    0{i + 1}
                  </span>
                </div>

                <h3 className="relative font-heading font-black text-xl md:text-2xl text-[#0F2D2A] leading-tight">
                  {s.title}
                </h3>
                <p className="relative mt-3 text-sm md:text-[15px] leading-relaxed text-[#1E4842]/85 flex-1">
                  {s.desc}
                </p>

                <a
                  href={waLink(msg)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={TEST_IDS.services.waCta(i)}
                  className="relative mt-7 inline-flex items-center justify-between gap-3 border-t border-[#0F2D2A]/10 pt-5 text-xs font-extrabold uppercase tracking-[0.16em] text-[#0F2D2A] group-hover:text-[#0EA5E9] transition-colors duration-200"
                >
                  Detaylı Bilgi Al
                  <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </a>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
